import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProcessStep {
  id: number;
  icon: string;
  color: string;
  bgColor: string;
}

export default function IntegrationProcess() {
  const { t } = useTranslation();
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const steps: ProcessStep[] = [
    { id: 1, icon: 'ri-shield-user-line', color: 'text-teal-600', bgColor: 'bg-teal-50' },
    { id: 2, icon: 'ri-file-list-3-line', color: 'text-amber-600', bgColor: 'bg-amber-50' },
    { id: 3, icon: 'ri-home-heart-line', color: 'text-green-600', bgColor: 'bg-green-50' },
    { id: 4, icon: 'ri-heart-pulse-line', color: 'text-rose-500', bgColor: 'bg-rose-50' },
    { id: 5, icon: 'ri-book-open-line', color: 'text-sky-600', bgColor: 'bg-sky-50' },
    { id: 6, icon: 'ri-community-line', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  ];

  const toggleStep = (stepId: number) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  return (
    <section id="integration" className="py-24 bg-gradient-to-b from-white to-[#F8FFFE]">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#1A2332] mb-4">
            {t('integration.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('integration.subtitle')}
          </p>
        </div>

        {/* Timeline Progress */}
        <div className="relative mb-16">
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-teal-200 via-amber-200 to-emerald-200 -translate-y-1/2 hidden lg:block"></div>
          <div className="flex justify-between items-center relative">
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center z-10">
                <div className={`w-16 h-16 rounded-full ${step.bgColor} ${step.color} flex items-center justify-center text-2xl mb-3 shadow-lg cursor-pointer hover:scale-110 transition-transform duration-300`}>
                  <i className={step.icon}></i>
                </div>
                <span className="text-xs font-semibold text-gray-700 text-center max-w-[80px]">
                  {t(`integration.steps.step${step.id}.shortTitle`)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Process Steps Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step) => (
            <div
              key={step.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer border border-gray-100"
              onClick={() => toggleStep(step.id)}
            >
              <div className={`${step.bgColor} p-6`}>
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-white ${step.color} flex items-center justify-center text-2xl shadow-md`}>
                    <i className={step.icon}></i>
                  </div>
                  <span className="text-xs font-bold text-gray-500 bg-white px-3 py-1 rounded-full">
                    {t('integration.step')} {step.id}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-2">
                  {t(`integration.steps.step${step.id}.title`)}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t(`integration.steps.step${step.id}.description`)}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">
                    <i className="ri-time-line mr-1"></i>
                    {t(`integration.steps.step${step.id}.duration`)}
                  </span>
                  <i className={`ri-arrow-${expandedStep === step.id ? 'up' : 'down'}-s-line ${step.color} text-xl`}></i>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedStep === step.id && (
                <div className="p-6 bg-white border-t border-gray-100">
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-[#1A2332] mb-3 flex items-center">
                      <i className="ri-file-text-line mr-2 text-[#4ECDC4]"></i>
                      {t('integration.requiredDocuments')}
                    </h4>
                    <ul className="space-y-2">
                      {t(`integration.steps.step${step.id}.documents`, { returnObjects: true } as any).map((doc: string, idx: number) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <i className="ri-checkbox-circle-fill text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-[#1A2332] mb-3 flex items-center">
                      <i className="ri-lightbulb-line mr-2 text-[#FFB84D]"></i>
                      {t('integration.tips')}
                    </h4>
                    <ul className="space-y-2">
                      {t(`integration.steps.step${step.id}.tips`, { returnObjects: true } as any).map((tip: string, idx: number) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <i className="ri-star-fill text-yellow-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <button className={`w-full py-3 ${step.color} bg-opacity-10 rounded-lg font-semibold text-sm hover:bg-opacity-20 transition-all duration-300 whitespace-nowrap`}>
                      {t('integration.getSupport')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Success Stories CTA */}
        <div className="mt-16 bg-gradient-to-r from-[#4ECDC4] to-[#44B8B0] rounded-2xl p-8 text-center shadow-xl">
          <h3 className="text-2xl font-bold text-white mb-3">
            {t('integration.successTitle')}
          </h3>
          <p className="text-white/95 mb-6 max-w-2xl mx-auto">
            {t('integration.successDescription')}
          </p>
          <button className="px-8 py-3 bg-white text-[#1A2332] rounded-full font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg">
            {t('integration.readStories')}
          </button>
        </div>
      </div>
    </section>
  );
}